import { ref, computed } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { isEmpty } from "@utils/toolBox";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@composables/handleError.js";
import { useSurveyStore } from "@stores/survey//survey";
import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";

export const useSurveyCompletionStore = defineStore("surveyCompletion", () => {
  // Stores
  const surveyStore = useSurveyStore();
  const surveyTargetsStore = useSurveyTargetsStore();
  const { token } = storeToRefs(surveyStore);
  const { countryOptions, targetCountries, currentTargets } = storeToRefs(surveyTargetsStore);

  // Completion data
  const surveyCompletionData = ref({});
  const cardsCompletionData = ref([]);
  const isCardDataAvailable = ref(true);
  const unavailableReason = ref(null);

  const fetchSurveyCompletionData = async () => {
    const params = { filters: getCleanFilters() };
    try {
      const { data, response } = await useMyFetch(`surveys/${token.value}/completion/survey_data`)
        .post(params, null)
        .json();

      if (!response.value.ok) return;

      const { cards_data, status, reason, ...summary } = data.value;
      isCardDataAvailable.value = status !== "unavailable";

      if (isCardDataAvailable.value) {
        surveyCompletionData.value = summary;
        cardsCompletionData.value = cards_data;
        unavailableReason.value = null;
        filterCompletionData();
      } else {
        surveyCompletionData.value = {};
        cardsCompletionData.value = [];
        unavailableReason.value = reason ?? null;
      }
    } catch (err) {
      handleError(err);
    }
  };

  const resetCompletionData = () => {
    surveyCompletionData.value = {};
    cardsCompletionData.value = [];
    isCardDataAvailable.value = true;
    unavailableReason.value = null;
  };

  // Completion filters
  const completionFilters = ref({
    dates: [],
    countries: [],
    targets: [],
    master_campaigns: [],
    campaigns: [],
    audiences: [],
    genders: [],
    min_screenout: 0,
    min_drop: 0,
    age: [0, 100],
  });

  const hasSurveyCampaigns = computed(() => filtersCampaignsOptions.value.length > 0);

  const getCleanFilters = () => {
    return Object.entries(completionFilters.value).reduce((acc, [key, value]) => {
      if (["min_drop", "min_screenout"].includes(key)) return acc;
      if (key === "age" && Array.isArray(value) && value.length === 2 && value[0] === 0 && value[1] === 100) return acc;
      if (!isEmpty(value)) acc[key] = value;
      return acc;
    }, {});
  };

  const filtersCountryOptions = computed(() =>
    countryOptions.value.filter(({ value }) => targetCountries.value.includes(value)),
  );
  const filtersTargetOptions = computed(() => currentTargets.value.map(({ id, name }) => ({ value: id, label: name })));
  const filtersMasterCampaignsOptions = ref([]);
  const filtersCampaignsOptions = ref([]);
  const filtersAudiencesOptions = ref([]);
  const filtersGenderOptions = ref([
    { label: "Men", value: "men" },
    { label: "Women", value: "women" },
  ]);

  const fetchAdminFiltersData = async () => {
    try {
      const { data, response } = await useMyFetch(`surveys/${token.value}/completion/filters_data`).post().json();

      if (response.value.ok) {
        filtersMasterCampaignsOptions.value = data.value.master_campaigns;
        filtersCampaignsOptions.value = data.value.campaigns;
        filtersAudiencesOptions.value = data.value.audiences;
      }
    } catch (err) {
      handleError(err);
    }
  };

  const resetCompletionFilters = (type = null) => {
    if (!type) {
      Object.keys(completionFilters.value).forEach((key) => resetCompletionFilters(key));
      return;
    }

    if (["dates", "countries", "targets", "master_campaigns", "campaigns", "audiences", "genders"].includes(type)) {
      completionFilters.value[type] = [];
    } else if (["min_drop", "min_screenout"].includes(type)) {
      completionFilters.value[type] = 0;
    } else if (type === "age") {
      completionFilters.value[type] = [0, 100];
    }
  };

  const applyCompletionFilters = async () => {
    await Promise.all([fetchSurveyCompletionData()]);
    filterCompletionData();
  };

  const formatPercentage = (value) => Math.round(value * 100) || 0;
  const filterCompletionData = () => {
    cardsCompletionData.value = cardsCompletionData.value.filter((card) => {
      const { screenout_rate, drop_rate } = card;
      return (
        (!completionFilters.value.min_screenout ||
          formatPercentage(screenout_rate) > completionFilters.value.min_screenout) &&
        (!completionFilters.value.min_drop || formatPercentage(drop_rate) > completionFilters.value.min_drop)
      );
    });
  };

  return {
    // Completion data
    surveyCompletionData,
    cardsCompletionData,
    isCardDataAvailable,
    unavailableReason,
    fetchSurveyCompletionData,
    resetCompletionData,
    // Completion filters
    completionFilters,
    hasSurveyCampaigns,
    filtersCountryOptions,
    filtersTargetOptions,
    filtersGenderOptions,
    filtersMasterCampaignsOptions,
    filtersCampaignsOptions,
    filtersAudiencesOptions,
    fetchAdminFiltersData,
    resetCompletionFilters,
    applyCompletionFilters,
  };
});
